// ─────────────────────────────────────────────────────────────
//  TrendChart  – Rolling forecast with confidence band
//  Budget / Actual / Forecast lines + shaded confidence interval
// ─────────────────────────────────────────────────────────────
import React from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

const COLORS = {
  budget:   "#4969ab",
  actual:   "#2d8c4e",
  forecast: "#e67e22",
  band:     "#e67e2230",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e0e6f0", borderRadius: 8,
      padding: "10px 16px", boxShadow: "0 4px 16px rgba(0,0,0,0.10)", fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#1a2340", fontSize: 13 }}>{label}</div>
      {payload.map((p) => (
        p.name !== "confidence_band" && (
          <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
            {p.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:{" "}
            <strong>₹{Number(p.value).toLocaleString("en-IN")}</strong>
          </div>
        )
      ))}
      {payload.find((p) => p.name === "confidence_band") && (
        <div style={{ color: COLORS.forecast, marginBottom: 2, fontSize: 11 }}>
          Confidence Range:{" "}
          ₹{Number(payload.find(p => p.name === "confidence_band")?.value?.[0]).toLocaleString("en-IN")}
          {" – "}
          ₹{Number(payload.find(p => p.name === "confidence_band")?.value?.[1]).toLocaleString("en-IN")}
        </div>
      )}
    </div>
  );
};

export default function TrendChart({
  data = [],
  title = "Rolling 12-Month Forecast",
  height = 320,
  showBudget = true,
  showActual = true,
  showForecast = true,
  showBand = true,
  currentMonthLabel = null,
  yLabel = "₹ Value",
}) {
  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      boxShadow: "0 2px 12px rgba(73,105,171,0.08)",
      padding: "18px 14px",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 14 }}>{yLabel}</div>

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#777" }} />
          <YAxis
            tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`}
            tick={{ fontSize: 11, fill: "#777" }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconType="circle" iconSize={9}
            formatter={(val) => (
              <span style={{ fontSize: 11, color: "#555" }}>
                {val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
            )}
          />

          {/* Confidence band (area between low/high) */}
          {showBand && (
            <Area
              type="monotone"
              dataKey="confidence_band"
              stroke="none"
              fill={COLORS.band}
              name="confidence_band"
              isAnimationActive={false}
            />
          )}

          {/* Current month divider */}
          {currentMonthLabel && (
            <ReferenceLine
              x={currentMonthLabel}
              stroke="#4969ab"
              strokeDasharray="6 3"
              label={{ value: "Today", position: "top", fontSize: 10, fill: "#4969ab" }}
            />
          )}

          {showBudget && (
            <Line
              type="monotone" dataKey="budget"
              stroke={COLORS.budget} strokeWidth={2}
              dot={{ r: 3 }} activeDot={{ r: 5 }}
              name="budget"
            />
          )}
          {showActual && (
            <Line
              type="monotone" dataKey="actual"
              stroke={COLORS.actual} strokeWidth={2.5}
              dot={{ r: 3 }} activeDot={{ r: 5 }}
              name="actual"
            />
          )}
          {showForecast && (
            <Line
              type="monotone" dataKey="forecast"
              stroke={COLORS.forecast} strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ r: 3 }} activeDot={{ r: 5 }}
              name="forecast"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
